import {
  type InterviewSlot,
  type InterviewSlotBatchRequest,
  type InterviewSlotBatchResult,
  type InterviewSlotCreateRequest,
  type InterviewSlotUpdateRequest,
  type InterviewSlotsListQuerySchema,
  type Paginated,
  type RecordInterviewOutcomeRequest,
  type RequestChangesRequest,
  type ScheduleInterviewRequest,
  type VerificationCaseDetail,
  type VerificationCaseSummary,
  type VerificationCasesListQuerySchema,
  type VerificationDecisionRequest,
  type VerificationQueueCounts,
  type VerificationStepKey,
  type WaiveStepRequest,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own input shape (U3) — never a hand-written copy of the filters. */
type QueueParams = z.input<typeof VerificationCasesListQuerySchema>;

/** The availability calendar's window + its three optional narrowings (D9b1). */
type InterviewSlotsParams = z.input<typeof InterviewSlotsListQuerySchema>;

/** Every mutation but the interview pair is addressed to one case. */
interface CaseRef {
  id: string;
}

/** Both tags: a write changes the case AND where it sits in the queue. */
const CASE_AND_QUEUE = (id: string) => [
  { type: 'VerificationCase' as const, id },
  'VerificationCases' as const,
];

/**
 * The staff review surface's data layer.
 *
 * ## One source for the case on screen, and it is the server
 *
 * Every write invalidates **both** tags — the case and the queue — and the
 * screen re-reads. The verdicts do return the freshly `reviewed()` detail, and
 * writing that response into the cache instead would save a round trip; it is
 * deliberately not done, for the reason `trustApi` gives about its own row:
 * rendering from a mutation response gives the screen two sources for one case,
 * and they disagree the moment a colleague acts a second later. `RenderGate`
 * already distinguishes `isLoading` from `isRefreshing`, so the re-read does not
 * blink — the case stays on screen while it happens.
 *
 * The **queue** is invalidated by every one of them too, because each changes
 * where the case belongs in the list, and the list is a different question
 * answered with its own filter and its own ordering.
 *
 * ## `decision` publishes; nothing else does
 *
 * An approval or a rejection moves `users.status` through `UserStatusService`
 * (N9), so the staff list, a member detail and anything else showing that account
 * are looking at a lifecycle state this just changed (W5). A step verdict, a
 * release and a request-for-changes all leave the member exactly where they were
 * — `pending_verification` — and announcing those would be telling every
 * subscriber about something that did not happen.
 */
export const verificationApi = createApi({
  reducerPath: 'verificationApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['VerificationCases', 'VerificationCase', 'InterviewSlots'],
  endpoints: (builder) => ({
    listCases: builder.query<Paginated<VerificationCaseSummary>, QueueParams>({
      query: (params) => ({ url: '/admin/verification/cases', method: 'GET', params }),
      providesTags: ['VerificationCases'],
    }),

    /** How long each work list is (F7) — refetched with every case write, like the lists. */
    queueCounts: builder.query<VerificationQueueCounts, void>({
      query: () => ({ url: '/admin/verification/queues', method: 'GET' }),
      providesTags: ['VerificationCases'],
    }),

    caseDetail: builder.query<VerificationCaseDetail, string>({
      query: (id) => ({ url: `/admin/verification/cases/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'VerificationCase' as const, id }],
    }),

    /** `submitted → in_review`. Records no owner — there is no per-reviewer claim. */
    openCase: builder.mutation<VerificationCaseDetail, CaseRef>({
      query: ({ id }) => ({ url: `/admin/verification/cases/${id}/open`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
    }),

    /** Back to the queue, undecided. */
    releaseCase: builder.mutation<VerificationCaseDetail, CaseRef>({
      query: ({ id }) => ({ url: `/admin/verification/cases/${id}/release`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
    }),

    /** Approve or reject. Terminal for this case, and it moves the member (N9). */
    decideCase: builder.mutation<VerificationCaseDetail, CaseRef & VerificationDecisionRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/verification/cases/${id}/decision`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          EVENT_BUS.publish({ type: 'members.status.changed', userId: data.userId });
        } catch {
          // Refused — nothing happened, so there is nothing to announce.
          // Swallowed deliberately: `onQueryStarted` has no caller, so a
          // rejection escaping it becomes an unhandled rejection. The failure is
          // already the caller's, through `unwrap()`.
        }
      },
    }),

    /** Named steps back to the member. They stay `pending_verification`. */
    requestChanges: builder.mutation<VerificationCaseDetail, CaseRef & RequestChangesRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/verification/cases/${id}/request-changes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
    }),

    /**
     * One step, judged or skipped.
     *
     * A single endpoint parameterised by `verdict` rather than three, because the
     * three differ only in the segment and in whether the reason is optional —
     * and the *permission* difference (`waive` is `verification.manage`) is the
     * screen's to enforce on the control, never the data layer's to encode.
     * Three near-identical mutations would be three places to fix a tag.
     */
    /** Every submitted evidence step passed in one act (F7); claims the case on the way. */
    acceptEvidence: builder.mutation<VerificationCaseDetail, CaseRef>({
      query: ({ id }) => ({
        url: `/admin/verification/cases/${id}/evidence/accept`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
    }),

    resolveStep: builder.mutation<
      VerificationCaseDetail,
      CaseRef & {
        stepKey: VerificationStepKey;
        verdict: 'pass' | 'fail' | 'waive';
      } & Partial<WaiveStepRequest>
    >({
      query: ({ id, stepKey, verdict, reason }) => ({
        url: `/admin/verification/cases/${id}/steps/${stepKey}/${verdict}`,
        method: 'POST',
        // `pass` takes an optional reason and the other two require one; the
        // schema on the server is the authority, so the body is sent as given.
        body: reason === undefined ? {} : { reason },
      }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
    }),

    scheduleInterview: builder.mutation<void, CaseRef & ScheduleInterviewRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/verification/cases/${id}/interview/schedule`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
    }),

    recordInterviewOutcome: builder.mutation<void, CaseRef & RecordInterviewOutcomeRequest>({
      query: ({ id, ...body }) => ({
        url: `/admin/verification/cases/${id}/interview/outcome`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => CASE_AND_QUEUE(id),
      // A passed interview may have approved the member (F7); the members
      // surfaces re-read the status the way they do after a decision. The
      // refetched detail says whether it did — nothing here guesses.
      async onQueryStarted({ id, outcome }, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          if (outcome !== 'passed') return;

          const detail = await dispatch(
            verificationApi.endpoints.caseDetail.initiate(id, { forceRefetch: true }),
          ).unwrap();
          if (detail.status === 'approved') {
            EVENT_BUS.publish({ type: 'members.status.changed', userId: detail.userId });
          }
        } catch {
          // Refused — nothing happened, so there is nothing to announce.
        }
      },
    }),

    /**
     * The interview availability calendar (D9b1) — windows staff publish for
     * members to book, not a fact about any one case.
     *
     * **One tag for the whole family**, unlike the per-case tag above: the
     * calendar is read a month at a time and every write can move what any month
     * shows — a batch spans weeks, and closing a window changes the day it sits
     * on — so a per-window tag would be five ids to get right for no saved
     * request. It is deliberately **separate** from `VerificationCase`: a
     * booking changes a case, and publishing a window does not.
     *
     * **Nothing here publishes on the event bus.** Opening or withdrawing a
     * window moves nobody's lifecycle state, which is the same line this file
     * already draws for a step verdict (W5).
     */
    listInterviewSlots: builder.query<InterviewSlot[], InterviewSlotsParams>({
      query: (params) => ({
        url: '/admin/verification/interview-slots',
        method: 'GET',
        params,
      }),
      providesTags: ['InterviewSlots'],
    }),

    createInterviewSlot: builder.mutation<InterviewSlot, InterviewSlotCreateRequest>({
      query: (body) => ({ url: '/admin/verification/interview-slots', method: 'POST', body }),
      invalidatesTags: ['InterviewSlots'],
    }),

    /**
     * A whole generated recurrence, in one act.
     *
     * The body carries the **expanded** windows rather than the rule that
     * produced them (`shared/utils/slot-recurrence` does the expanding), and the
     * reply says what was created *and* what was passed over — "nineteen of your
     * twenty-four" is a fact the dialog has to be able to state.
     */
    createInterviewSlotBatch: builder.mutation<InterviewSlotBatchResult, InterviewSlotBatchRequest>(
      {
        query: (body) => ({
          url: '/admin/verification/interview-slots/batch',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['InterviewSlots'],
      },
    ),

    updateInterviewSlot: builder.mutation<
      InterviewSlot,
      { slotId: string; body: InterviewSlotUpdateRequest }
    >({
      query: ({ slotId, body }) => ({
        url: `/admin/verification/interview-slots/${slotId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['InterviewSlots'],
    }),

    /** Refused while live interviews name the window — close it instead. */
    deleteInterviewSlot: builder.mutation<void, { slotId: string }>({
      query: ({ slotId }) => ({
        url: `/admin/verification/interview-slots/${slotId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['InterviewSlots'],
    }),
  }),
});

export const {
  useListCasesQuery,
  useQueueCountsQuery,
  useCaseDetailQuery,
  useOpenCaseMutation,
  useReleaseCaseMutation,
  useDecideCaseMutation,
  useRequestChangesMutation,
  useAcceptEvidenceMutation,
  useResolveStepMutation,
  useScheduleInterviewMutation,
  useRecordInterviewOutcomeMutation,
  useListInterviewSlotsQuery,
  useCreateInterviewSlotMutation,
  useCreateInterviewSlotBatchMutation,
  useUpdateInterviewSlotMutation,
  useDeleteInterviewSlotMutation,
} = verificationApi;
